import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { UserCircle, Shield, HelpCircle } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useExternalUserFormWizard } from '../hooks/useExternalUserFormWizard';
import { useCreateExternalUser } from '../hooks/useExternalUsers';
import { useAssignSocietyToExternalUser } from '../hooks/useExternalUserSocieties';
import { WizardLayout } from '@/Components/shared/WizardLayout';
import { Step1Informations } from './wizard-steps/Step1Informations';
import { Step2Role } from './wizard-steps/Step2Role';
import { useAuthStore } from '@/Stores/auth-v2-store';
import { useToggleTutorials } from '@/hooks/useToggleTutorials';
import { useSociety, useSocietyChildren } from '@/features/societies/hooks/useSocieties';
import { useExternalUserTutorial, getFilteredExternalUserTutorialSteps } from '../tutorials';
import { TutorialIntro } from '../tutorials';
import { TutorialSpotlight } from '@/features/warranties/tutorials/components/TutorialSpotlight';
import { useWizardBrowserBack } from '@/hooks/useWizardBrowserBack';
import type { WizardStepHandle } from './wizard-steps/types';

const STEPS = [
  { number: 1, label: 'Informations', icon: <UserCircle className="tw-w-6 tw-h-6" /> },
  { number: 2, label: 'Rôle', icon: <Shield className="tw-w-6 tw-h-6" /> },
];

interface ExternalUserFormWizardProps {
  /** Callback après création réussie (reçoit l'ID du nouvel utilisateur) */
  onComplete: (userId: string) => void;
  /** Pré-sélectionner une société */
  initialSocietyId?: string;
  /** Mode embarqué dans un onglet (stepper/footer sticky au lieu de fixed) */
  embedded?: boolean;
}

export const ExternalUserFormWizard: React.FC<ExternalUserFormWizardProps> = ({
  onComplete,
  initialSocietyId,
  embedded,
}) => {
  const { currentUser } = useAuthStore();
  const queryClient = useQueryClient();
  const createMutation = useCreateExternalUser();
  const assignSocietyMutation = useAssignSocietyToExternalUser();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: userSociety } = useSociety(currentUser?.societyId);
  const { data: childrenData } = useSocietyChildren(currentUser?.societyId || '');

  const shouldShowSocietyStep = useMemo(() => {
    const hasChildren = (childrenData || []).length > 0;
    return currentUser?.type === 'INTERNAL' || hasChildren;
  }, [currentUser?.type, childrenData]);

  const isExternalRootSociety = currentUser?.type === 'EXTERNAL' && !!userSociety && !userSociety.parentId;

  const allowedSocietyIds = useMemo(() => {
    if (currentUser?.type !== 'EXTERNAL' || !currentUser.societyId || !childrenData?.length) return undefined;
    return [currentUser.societyId, ...childrenData.map(c => c.uuid)];
  }, [currentUser?.type, currentUser?.societyId, childrenData]);

  const groupSocieties = useMemo(() => {
    if (currentUser?.type !== 'EXTERNAL' || !childrenData?.length) return undefined;
    return childrenData.map(c => ({ uuid: c.uuid, name: c.name, code: c.code, city: c.city }));
  }, [currentUser?.type, childrenData]);

  const {
    currentStep,
    formData,
    touchedSteps,
    updateFormData,
    goToNextStep,
    goToPreviousStep,
    goToStep,
  } = useExternalUserFormWizard(initialSocietyId, currentUser?.societyId);

  // Protection bouton retour navigateur (active uniquement à partir du step 2) :
  // - back en step 2 → retour au step 1, données préservées
  // - step 1 → comportement navigateur natif (l'utilisateur peut quitter librement)
  // - beforeunload (refresh / fermeture tab) → alerte navigateur native (step 2)
  useWizardBrowserBack({
    currentStep,
    goToPreviousStep,
    enabled: currentStep > 1,
    beforeUnloadEnabled: !isSubmitting,
  });

  const isSocietyLocked = !!initialSocietyId;
  const effectiveSocietyLocked = isSocietyLocked || isExternalRootSociety;

  const hasInitializedSocietyRef = useRef(false);
  useEffect(() => {
    if (!userSociety) return;
    if (hasInitializedSocietyRef.current) return;
    hasInitializedSocietyRef.current = true;

    if (shouldShowSocietyStep && !effectiveSocietyLocked && currentUser?.type === 'EXTERNAL') {
      updateFormData({ primarySocietyId: '', additionalSocietyIds: [] });
    }
  }, [shouldShowSocietyStep, effectiveSocietyLocked, userSociety, currentUser?.type]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Soumission ---
  const handleSubmit = useCallback(async (sendWelcomeEmail: boolean) => {
    setIsSubmitting(true);

    try {
      const createData = {
        civility: formData.civility,
        firstname: formData.firstname,
        lastname: formData.lastname,
        email: formData.email,
        phone: formData.phone,
        position: formData.position,
        roleType: formData.roleType,
        permissions: formData.permissions,
        societyId: formData.primarySocietyId,
        sendWelcomeEmail,
      };

      const result = await createMutation.mutateAsync(createData);
      const userId = result.id;

      // Assigner les sociétés additionnelles (si présentes)
      if (formData.additionalSocietyIds.length > 0) {
        try {
          await Promise.all(
            formData.additionalSocietyIds.map((societyId) =>
              assignSocietyMutation.mutateAsync({ userId, societyId, isPrimary: false })
            )
          );
        } catch (assignError) {
          console.error('Erreur assignation sociétés:', assignError);
        }
      }

      // Invalider le cache de la société si pré-sélectionnée
      if (initialSocietyId) {
        queryClient.invalidateQueries({ queryKey: ['society-users', initialSocietyId] });
      }

      onComplete(userId);
    } catch (error) {
      console.error('Erreur création utilisateur:', error);
      setIsSubmitting(false);
    }
  }, [formData, createMutation, assignSocietyMutation, queryClient, initialSocietyId, onComplete]);

  // --- Tutoriel (uniquement pour les utilisateurs EXTERNAL) ---
  const isExternalUser = currentUser?.type === 'EXTERNAL';
  const isTutorialEnabled = isExternalUser && currentUser?.showTutorials !== false;
  const { enableTutorials, disableTutorials } = useToggleTutorials();

  const {
    state: tutorialState,
    startTutorial,
    nextStep: nextTutorialStep,
    previousStep: previousTutorialStep,
    skipTutorial,
    completeTutorial,
    restartAtStep,
    showIntro,
  } = useExternalUserTutorial();

  const tutorialSteps = useMemo(() => {
    return getFilteredExternalUserTutorialSteps(shouldShowSocietyStep);
  }, [shouldShowSocietyStep]);

  useEffect(() => {
    if (isTutorialEnabled && !tutorialState.hasBeenCompleted && !tutorialState.hasBeenSkipped && !tutorialState.showIntro && !tutorialState.isActive) {
      const timer = setTimeout(() => {
        showIntro();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isTutorialEnabled, tutorialState.hasBeenCompleted, tutorialState.hasBeenSkipped, tutorialState.showIntro, tutorialState.isActive, showIntro]);

  const currentTutorialStep = useMemo(() => {
    if (!tutorialState.isActive) return null;
    return tutorialSteps[tutorialState.currentStepIndex];
  }, [tutorialState.isActive, tutorialState.currentStepIndex, tutorialSteps]);

  const isTutorialFieldValid = useMemo(() => {
    if (!currentTutorialStep) return false;
    return currentTutorialStep.validation(formData);
  }, [currentTutorialStep, formData]);

  const firstStepIndexForCurrentWizard = useMemo(() => {
    return tutorialSteps.findIndex(step => step.wizardStep === currentStep);
  }, [tutorialSteps, currentStep]);

  const handleGoToStep = useCallback((step: number) => {
    goToStep(step);
    if (tutorialState.isActive) {
      const firstIndex = tutorialSteps.findIndex(s => s.wizardStep === step);
      if (firstIndex !== -1) {
        restartAtStep(firstIndex);
      }
    }
  }, [goToStep, tutorialState.isActive, tutorialSteps, restartAtStep]);

  const handleNextTutorialStep = useCallback(() => {
    if (tutorialState.currentStepIndex >= tutorialSteps.length - 1) {
      disableTutorials();
      completeTutorial();
      goToNextStep();
    } else {
      nextTutorialStep();

      const nextStep = tutorialSteps[tutorialState.currentStepIndex + 1];
      if (nextStep && nextStep.wizardStep > currentStep) {
        goToNextStep();
      }
    }
  }, [
    tutorialState.currentStepIndex,
    tutorialSteps,
    disableTutorials,
    completeTutorial,
    nextTutorialStep,
    currentStep,
    goToNextStep,
  ]);

  const handlePreviousTutorialStep = useCallback(() => {
    if (tutorialState.currentStepIndex > 0) {
      previousTutorialStep();

      const prevStep = tutorialSteps[tutorialState.currentStepIndex - 1];
      if (prevStep && prevStep.wizardStep < currentStep) {
        goToPreviousStep();
      }
    }
  }, [
    tutorialState.currentStepIndex,
    tutorialSteps,
    previousTutorialStep,
    currentStep,
    goToPreviousStep,
  ]);

  // --- UI ---
  const stepRef = useRef<WizardStepHandle>(null);

  const handleNext = useCallback(() => {
    stepRef.current?.handleNext();
  }, []);

  const [canProceed, setCanProceed] = React.useState(true);

  const nextLabel = currentStep === 2 ? "Créer l'utilisateur" : 'Suivant';

  return (
    <>
      <WizardLayout
        steps={STEPS}
        currentStep={currentStep}
        completedSteps={touchedSteps}
        onStepClick={handleGoToStep}
        embedded={embedded}

        onPrevious={currentStep > 1 ? goToPreviousStep : undefined}
        onNext={handleNext}
        nextLabel={nextLabel}
        canProceed={canProceed}
        isSubmitting={isSubmitting}
        hideWhenTutorial
        isTutorialActive={tutorialState.isActive}
        helpAction={!tutorialState.isActive && !tutorialState.showIntro && isExternalUser && firstStepIndexForCurrentWizard !== -1 ? (
          <button
            type="button"
            onClick={() => { enableTutorials(); restartAtStep(firstStepIndexForCurrentWizard); }}
            className="tw-flex tw-items-center tw-gap-2 tw-px-3 sm:tw-px-5 tw-py-3 tw-bg-white tw-text-affinicar-primary tw-font-medium tw-rounded-xl tw-border tw-border-affinicar-lighter hover:tw-bg-affinicar-lighter tw-transition-colors"
            title="Relancer le tutoriel à partir de cette étape"
          >
            <HelpCircle className="tw-w-4 tw-h-4" />
            <span className="tw-hidden sm:tw-inline">Aide</span>
          </button>
        ) : undefined}
      >
        {currentStep === 1 && (
          <Step1Informations
            ref={stepRef}
            formData={formData}
            updateFormData={updateFormData}
            onNext={goToNextStep}
            shouldShowSocietyStep={shouldShowSocietyStep}
            isSocietyLocked={effectiveSocietyLocked}
            allowedSocietyIds={allowedSocietyIds}
            groupSocieties={groupSocieties}
            onCanProceedChange={setCanProceed}
          />
        )}

        {currentStep === 2 && (
          <Step2Role
            ref={stepRef}
            formData={formData}
            updateFormData={updateFormData}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            onCanProceedChange={setCanProceed}
          />
        )}
      </WizardLayout>

      {/* Introduction du tutoriel */}
      {tutorialState.showIntro && isTutorialEnabled && (
        <TutorialIntro
          onStart={() => startTutorial(firstStepIndexForCurrentWizard !== -1 ? firstStepIndexForCurrentWizard : 0)}
          onSkip={() => { disableTutorials(); skipTutorial(); }}
        />
      )}

      {/* Tutoriel interactif */}
      {tutorialState.isActive && currentTutorialStep && isTutorialEnabled && (
        <TutorialSpotlight
          currentStep={currentTutorialStep as any}
          stepNumber={tutorialState.currentStepIndex + 1}
          totalSteps={tutorialSteps.length}
          isFieldValid={isTutorialFieldValid}
          onNext={handleNextTutorialStep}
          onPrevious={handlePreviousTutorialStep}
          onSkip={() => { disableTutorials(); skipTutorial(); }}
          placement={currentTutorialStep.placement}
        />
      )}
    </>
  );
};
